import api from './api';
import {
  mapEquipmentStateCatalogMutation,
  mapEquipmentStateCatalogRebase,
  mapEquipmentStateCatalogVersion,
  mapEquipmentStateCatalogWorkspace,
  mapEquipmentStateIssue,
  unwrapEquipmentStateCatalogData,
} from './prodEquipmentStateCatalogMappers';
import type {
  EquipmentStateBinding,
  EquipmentStateCatalogMutationResult,
  EquipmentStateCatalogRebaseResult,
  EquipmentStateCatalogValidationResult,
  EquipmentStateCatalogVersion,
  EquipmentStateCatalogWorkspace,
  EquipmentStateClassDraft,
  EquipmentStateClassModel,
} from '../types/prodEquipmentStateCatalog';

const ROOT = '/prod/equipment-state-catalogs';

type RawRecord = Record<string, unknown>;

const asRecord = (value: unknown): RawRecord => (
  value && typeof value === 'object' ? value as RawRecord : {}
);

export { normalizeEquipmentStateCatalogError } from './prodEquipmentStateCatalogErrors';

export const prodEquipmentStateCatalogApi = {
  async listVersions(facilityCode: string): Promise<EquipmentStateCatalogVersion[]> {
    const response = await api.get(ROOT, { params: { facilityCode } });
    const data = unwrapEquipmentStateCatalogData(response.data);
    return Array.isArray(data) ? data.map(mapEquipmentStateCatalogVersion) : [];
  },

  async createDraft(input: {
    facilityCode: string;
    name?: string;
    resourceTopologyVersionId: string;
    baseVersionId?: string;
  }): Promise<EquipmentStateCatalogVersion> {
    const response = await api.post(ROOT, input);
    return mapEquipmentStateCatalogVersion(unwrapEquipmentStateCatalogData(response.data));
  },

  async getVersion(versionId: string): Promise<EquipmentStateCatalogVersion> {
    const response = await api.get(`${ROOT}/${versionId}`);
    return mapEquipmentStateCatalogVersion(unwrapEquipmentStateCatalogData(response.data));
  },

  async getWorkspace(versionId: string): Promise<EquipmentStateCatalogWorkspace> {
    const response = await api.get(`${ROOT}/${versionId}/workspace`);
    return mapEquipmentStateCatalogWorkspace(response.data);
  },

  async createClass(
    versionId: string,
    revision: number,
    draft: EquipmentStateClassDraft,
  ): Promise<EquipmentStateCatalogMutationResult> {
    const response = await api.post(`${ROOT}/${versionId}/classes`, { revision, ...draft });
    return mapEquipmentStateCatalogMutation(response.data);
  },

  async updateClass(
    versionId: string,
    classId: string,
    revision: number,
    patch: Omit<EquipmentStateClassDraft, 'code'>,
  ): Promise<EquipmentStateCatalogMutationResult> {
    const response = await api.patch(`${ROOT}/${versionId}/classes/${classId}`, { revision, ...patch });
    return mapEquipmentStateCatalogMutation(response.data);
  },

  async retireClass(
    versionId: string,
    classId: string,
    revision: number,
  ): Promise<EquipmentStateCatalogMutationResult> {
    const response = await api.post(`${ROOT}/${versionId}/classes/${classId}/retire`, { revision });
    return mapEquipmentStateCatalogMutation(response.data);
  },

  async replaceClassModel(
    versionId: string,
    classId: string,
    revision: number,
    model: EquipmentStateClassModel,
  ): Promise<EquipmentStateCatalogMutationResult> {
    const response = await api.put(`${ROOT}/${versionId}/classes/${classId}/model`, {
      revision,
      ...model,
    });
    return mapEquipmentStateCatalogMutation(response.data);
  },

  async replaceBindings(
    versionId: string,
    revision: number,
    bindings: EquipmentStateBinding[],
  ): Promise<EquipmentStateCatalogMutationResult> {
    const response = await api.put(`${ROOT}/${versionId}/bindings`, { revision, bindings });
    return mapEquipmentStateCatalogMutation(response.data);
  },

  async rebaseTopology(
    versionId: string,
    revision: number,
    resourceTopologyVersionId: string,
  ): Promise<EquipmentStateCatalogRebaseResult> {
    const response = await api.post(`${ROOT}/${versionId}/rebase-topology`, {
      revision,
      resourceTopologyVersionId,
    });
    return mapEquipmentStateCatalogRebase(response.data);
  },

  async validate(
    versionId: string,
    revision: number,
  ): Promise<EquipmentStateCatalogValidationResult> {
    const response = await api.post(`${ROOT}/${versionId}/validate`, { revision });
    const raw = asRecord(unwrapEquipmentStateCatalogData(response.data));
    const issues = Array.isArray(raw.issues) ? raw.issues.map(mapEquipmentStateIssue) : [];
    return {
      version: mapEquipmentStateCatalogVersion(raw.version),
      issues,
    };
  },

  async publish(versionId: string, revision: number): Promise<EquipmentStateCatalogVersion> {
    const response = await api.post(`${ROOT}/${versionId}/publish`, { revision });
    return mapEquipmentStateCatalogVersion(unwrapEquipmentStateCatalogData(response.data));
  },

  async abandon(versionId: string, revision: number): Promise<EquipmentStateCatalogVersion> {
    const response = await api.post(`${ROOT}/${versionId}/abandon`, { revision });
    return mapEquipmentStateCatalogVersion(unwrapEquipmentStateCatalogData(response.data));
  },
};
